import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { clientsAPI, tagsAPI, customFieldsAPI, teamAPI, lookupValuesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Search, Filter, Mail, Phone, Calendar, FileText, ArrowUpDown, Loader2, Plus, Download, X, Trash2, ChevronDown, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import AddressForm from '../components/AddressForm';
import ConditionalField from '../components/ConditionalField';
import ValidatedInput from '../components/ValidatedInput';
import ClientSearchInput from '../components/ClientSearchInput';
import * as XLSX from 'xlsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';

// Helper function to format name to Title Case (First Letter Capital)
const formatToTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to format SIN number (XXX-XXX-XXX)
const formatSIN = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Helper function to format phone number ((XXX) XXX-XXXX)
const formatPhoneNumber = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Helper function to format email to lowercase
const formatEmail = (value) => {
  if (!value) return '';
  return value.toLowerCase();
};

const Clients = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [tags, setTags] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(['active', 'pending']); // Default: show active and pending, hide inactive
  const [typeFilter, setTypeFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [referralSourceFilter, setReferralSourceFilter] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  
  // Create client dialog
  const [clientDialog, setClientDialog] = useState(false);
  const [individualClients, setIndividualClients] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '', contactPerson: '', email: '', phone: '',
    address: { line1: '', line2: '', city: '', province: '', country: '', postalCode: '' },
    type: 'Individual', status: 'active',
    industry: '', referralSource: '',
    sin: '', dateOfBirth: '', maritalStatus: '',
    spouse: { name: '', email: '', phone: '', sin: '', dateOfBirth: '' },
    children: [],
    gstHstNumber: '', payrollNumber: '',
    businessNumber: '', ownerId: '', yearEnd: '',
    customFields: {}
  });

  useEffect(() => {
    fetchTags();
    fetchCustomFields();
    fetchTeamMembers();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchClients();
  }, [searchQuery, selectedStatuses, typeFilter, assignedToFilter, referralSourceFilter, selectedTags, sortBy, currentPage, pageSize]);
  
  // Reset to page 1 when filters or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStatuses, typeFilter, assignedToFilter, referralSourceFilter, selectedTags, sortBy, pageSize]);

  const fetchTags = async () => {
    try {
      const response = await tagsAPI.getAll();
      setTags(response.data.tags || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await customFieldsAPI.getAll();
      setCustomFields(response.data.fields || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await teamAPI.getMembers();
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      // Fetch industries and referral sources from lookup values API
      const response = await lookupValuesAPI.getAll();
      setIndustries(response.data.industries || []);
      setReferralSources(response.data.referralSources || []);
    } catch (error) {
      console.error('Error fetching lookup values:', error);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {
        limit: pageSize,
        skip: (currentPage - 1) * pageSize
      };
      if (searchQuery) params.search = searchQuery;
      // Multi-select status filter
      if (selectedStatuses.length > 0 && selectedStatuses.length < 3) {
        params.status = selectedStatuses.join(',');
      }
      if (typeFilter !== 'all') params.type = typeFilter;
      if (assignedToFilter !== 'all') params.assigned_to = assignedToFilter;
      if (referralSourceFilter !== 'all') params.referral_source = referralSourceFilter;
      if (selectedTags.length > 0) params.tags = selectedTags.join(',');
      if (sortBy) params.sort_by = sortBy;
      
      const response = await clientsAPI.getAll(params);
      setClients(response.data.clients || []);
      setTotalClients(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tagName) => {
    setSelectedTags(prev => 
      prev.includes(tagName)
        ? prev.filter(t => t !== tagName)
        : [...prev, tagName]
    );
  };

  const clearAllTags = () => {
    setSelectedTags([]);
  };

  const handleDeleteClick = (e, client) => {
    e.stopPropagation(); // Prevent row click navigation
    setClientToDelete(client);
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!clientToDelete) return;
    
    setDeleting(true);
    try {
      await clientsAPI.delete(clientToDelete._id);
      toast({ title: 'Success', description: `Client "${clientToDelete.name}" deleted successfully` });
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to delete client', 
        variant: 'destructive' 
      });
    } finally {
      setDeleting(false);
      setDeleteDialog(false);
      setClientToDelete(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const openCreateDialog = async () => {
    // Fetch individual clients for corporation owner linking
    try {
      const response = await clientsAPI.getAll({ type: 'Individual' });
      setIndividualClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching individual clients:', error);
    }
    // Refresh custom fields
    await fetchCustomFields();
    // Reset custom field values
    setNewClient(prev => ({ ...prev, customFields: {} }));
    setClientDialog(true);
  };

  const updateCustomField = (fieldId, value) => {
    setNewClient(prev => ({
      ...prev,
      customFields: { ...prev.customFields, [fieldId]: value }
    }));
  };

  const getApplicableCustomFields = () => {
    const clientType = newClient.type;
    return customFields.filter(field => {
      // If appliesTo is empty or not set, show for all types
      if (!field.appliesTo || field.appliesTo.length === 0) return true;
      // Check if the client type is in the appliesTo list
      return field.appliesTo.includes(clientType);
    });
  };

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.email) {
      toast({ title: 'Error', description: 'Name and email are required', variant: 'destructive' });
      return;
    }
    
    try {
      // Determine if spouse/children data should be included (not for single marital status)
      const shouldIncludeFamily = newClient.type === 'Individual' && 
        newClient.maritalStatus && 
        newClient.maritalStatus !== 'Single';
      
      const clientData = {
        ...newClient,
        tags: [],
        customFields: newClient.customFields || {},
        spouse: shouldIncludeFamily && newClient.spouse?.name ? newClient.spouse : null,
        children: shouldIncludeFamily ? newClient.children : [],
        address: newClient.address.line1 ? newClient.address : null
      };
      
      const response = await clientsAPI.create(clientData);
      toast({ title: 'Success', description: `Client "${newClient.name}" created` });
      setClientDialog(false);
      setNewClient({
        name: '', contactPerson: '', email: '', phone: '',
        address: { line1: '', line2: '', city: '', province: '', country: '', postalCode: '' },
        type: 'Individual', status: 'active', industry: '', referralSource: '',
        sin: '', dateOfBirth: '', maritalStatus: '',
        spouse: { name: '', email: '', phone: '', sin: '', dateOfBirth: '' },
        children: [],
        gstHstNumber: '', payrollNumber: '',
        businessNumber: '', ownerId: '', yearEnd: '',
        customFields: {}
      });
      fetchClients();
      
      if (response.data._id) {
        navigate(`/clients/${response.data._id}`);
      }
    } catch (error) {
      console.error('Error creating client:', error);
      toast({ title: 'Error', description: 'Failed to create client', variant: 'destructive' });
    }
  };

  const updateSpouse = (field, value) => {
    setNewClient(prev => ({
      ...prev,
      spouse: { ...prev.spouse, [field]: value }
    }));
  };

  const addChild = () => {
    setNewClient(prev => ({
      ...prev,
      children: [...prev.children, { name: '', dateOfBirth: '', sin: '' }]
    }));
  };

  const removeChild = (index) => {
    setNewClient(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index)
    }));
  };

  const updateChild = (index, field, value) => {
    setNewClient(prev => ({
      ...prev,
      children: prev.children.map((child, i) => 
        i === index ? { ...child, [field]: value } : child
      )
    }));
  };

  // Helper to get display name with spouse
  const getDisplayName = (client) => {
    if (client.type === 'Individual' && client.spouse?.name) {
      return `${client.name} & ${client.spouse.name}`;
    }
    return client.name;
  };

  // Get team member name from ID
  const getTeamMemberName = (id) => {
    const member = teamMembers.find(m => m._id === id);
    return member?.name || id || 'Unassigned';
  };

  // Export clients to Excel
  const exportToExcel = () => {
    if (clients.length === 0) {
      toast({ title: 'No Data', description: 'No clients to export', variant: 'destructive' });
      return;
    }

    const data = clients.map(client => ({
      'Client Name': client.name,
      'Contact Person': client.contactPerson || '',
      'Email': client.email || '',
      'Phone': client.phone || '',
      'Type': client.type || '',
      'Status': client.status || '',
      'Industry': client.industry || '',
      'Referral Source': client.referralSource || '',
      'Assigned To': getTeamMemberName(client.assignedTo),
      'Address': client.address ? `${client.address.line1 || ''}, ${client.address.city || ''}, ${client.address.province || ''} ${client.address.postalCode || ''}`.trim() : '',
      'Open Tasks': client.openTasks || 0,
      'Invoiced': client.totalInvoiced || 0,
      'Collected': client.revenue || 0,
      'Date Added': client.createdAt ? format(new Date(client.createdAt), 'MMM dd, yyyy') : '',
      'SIN': client.sin || '',
      'Business Number': client.businessNumber || '',
      'GST/HST Number': client.gstHstNumber || '',
      'Year End': client.yearEnd || '',
      // Spouse details
      'Spouse Name': client.spouse?.name || '',
      'Spouse Email': client.spouse?.email || '',
      'Spouse Phone': client.spouse?.phone || '',
      'Spouse SIN': client.spouse?.sin || ''
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 25 }, // Client Name
      { wch: 20 }, // Contact Person
      { wch: 25 }, // Email
      { wch: 15 }, // Phone
      { wch: 12 }, // Type
      { wch: 10 }, // Status
      { wch: 15 }, // Industry
      { wch: 15 }, // Referral Source
      { wch: 20 }, // Assigned To
      { wch: 40 }, // Address
      { wch: 10 }, // Open Tasks
      { wch: 12 }, // Invoiced
      { wch: 12 }, // Collected
      { wch: 12 }, // Date Added
      { wch: 12 }, // SIN
      { wch: 15 }, // Business Number
      { wch: 15 }, // GST/HST Number
      { wch: 12 }, // Year End
      { wch: 20 }, // Spouse Name
      { wch: 25 }, // Spouse Email
      { wch: 15 }, // Spouse Phone
      { wch: 12 }  // Spouse SIN
    ];
    
    // Format Invoiced and Collected columns with currency
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 1; R <= range.e.r; R++) {
      ['L', 'M'].forEach(col => { // Invoiced (L) and Collected (M)
        const cell = ws[col + (R + 1)];
        if (cell && typeof cell.v === 'number') {
          cell.z = '$#,##0.00';
        }
      });
    }
    
    XLSX.writeFile(wb, `Clients_Export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast({ title: 'Success', description: `Exported ${clients.length} clients to Excel` });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-500 mt-1">Manage and view all your clients</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={exportToExcel} data-testid="export-clients-btn">
            <Download className="h-4 w-4" />
            Export Excel
          </Button>
          <Button className="gap-2" onClick={openCreateDialog}>
            <Plus className="h-4 w-4" />
            Add New Client
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 flex-wrap items-center">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search clients by name, email or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Status Filter - Multi-select Dropdown */}
            <Popover open={statusDropdownOpen} onOpenChange={setStatusDropdownOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[180px] justify-between">
                  <span>
                    {selectedStatuses.length === 0 
                      ? 'Select Status' 
                      : selectedStatuses.length === 3 
                        ? 'All Status' 
                        : selectedStatuses.map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(', ')}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[180px] p-2" align="start">
                <div className="space-y-2">
                  {['active', 'pending', 'inactive'].map((status) => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status}`}
                        checked={selectedStatuses.includes(status)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedStatuses(prev => [...prev, status]);
                          } else {
                            setSelectedStatuses(prev => prev.filter(s => s !== status));
                          }
                        }}
                      />
                      <label
                        htmlFor={`status-${status}`}
                        className="text-sm font-medium leading-none cursor-pointer capitalize"
                      >
                        {status}
                      </label>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setSelectedStatuses(['active', 'pending', 'inactive'])}
                    >
                      Select All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => setSelectedStatuses(['active', 'pending'])}
                    >
                      Active & Pending Only
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Individual">Individual</SelectItem>
                <SelectItem value="Corporation">Corporation</SelectItem>
                <SelectItem value="Others">Others</SelectItem>
              </SelectContent>
            </Select>

            {/* Assigned To Filter */}
            <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Assigned To" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team</SelectItem>
                {teamMembers.map((member) => (
                  <SelectItem key={member._id} value={member.name}>{member.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Tag Filter - Multiple Selection */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Filter by Tags:</span>
                {selectedTags.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllTags}
                    className="h-6 text-xs"
                  >
                    Clear All
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => {
                  const isSelected = selectedTags.includes(tag.name);
                  return (
                    <Badge
                      key={tag._id}
                      onClick={() => toggleTag(tag.name)}
                      className="cursor-pointer transition-all"
                      style={{
                        backgroundColor: isSelected ? tag.color : 'transparent',
                        color: isSelected ? 'white' : tag.color,
                        border: `2px solid ${tag.color}`
                      }}
                    >
                      {tag.name}
                    </Badge>
                  );
                })}
              </div>
            </div>

            {/* Referral Source Filter - Dropdown */}
            {referralSources.length > 0 && (
              <Select value={referralSourceFilter} onValueChange={setReferralSourceFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Referral Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {referralSources.map((source) => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Sort */}
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-gray-500" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name (A-Z)</SelectItem>
                  <SelectItem value="date">Date Added (Newest)</SelectItem>
                  <SelectItem value="date_oldest">Date Added (Oldest)</SelectItem>
                  <SelectItem value="activity">Last Activity</SelectItem>
                  <SelectItem value="type">Type</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Open Tasks</TableHead>
                  <TableHead>Invoiced</TableHead>
                  <TableHead>Collected</TableHead>
                  <TableHead>Date Added</TableHead>
                  <TableHead className="w-16">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                <TableRow 
                  key={client._id} 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/clients/${client._id}`)}
                >
                  <TableCell>
                    <span className="font-mono text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {client.clientCode || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold">
                        {client.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900" data-testid={`client-name-${client._id}`}>
                          {getDisplayName(client)}
                        </p>
                        <p className="text-sm text-gray-500">{client.contactPerson}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm text-gray-700">
                      <Phone className="h-3.5 w-3.5 text-gray-400" />
                      {client.phone || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Mail className="h-3.5 w-3.5 text-gray-400" />
                      {client.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(client.status)}>
                      {client.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-700">{client.type}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-700">{client.assignedTo}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium text-gray-900">{client.openTasks}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium text-indigo-600">
                      ${(client.totalInvoiced || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium text-green-600">
                      ${(client.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600">
                      {format(new Date(client.createdAt), 'MMM dd, yyyy')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => handleDeleteClick(e, client)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {clients.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-gray-500">
                    No clients found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
        
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t mb-12">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              Showing {totalClients > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} to {Math.min(currentPage * pageSize, totalClients)} of {totalClients} clients
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Show:</span>
              <Select value={pageSize.toString()} onValueChange={(v) => setPageSize(Number(v))}>
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="300">300</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {totalClients > pageSize && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.ceil(totalClients / pageSize) }, (_, i) => i + 1)
                  .filter(page => {
                    const totalPages = Math.ceil(totalClients / pageSize);
                    if (totalPages <= 7) return true;
                    if (page === 1 || page === totalPages) return true;
                    if (Math.abs(page - currentPage) <= 1) return true;
                    if (page === currentPage - 2 || page === currentPage + 2) return '...';
                    return false;
                  })
                  .map((page, idx, arr) => {
                    if (page === '...') {
                      return <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>;
                    }
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? 'default' : 'outline'}
                        size="sm"
                        className="w-9 h-9 p-0"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    );
                  })
                }
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(Math.ceil(totalClients / pageSize), p + 1))}
                disabled={currentPage >= Math.ceil(totalClients / pageSize)}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
        </CardContent>
      </Card>

      {/* Create Client Dialog */}
      <Dialog open={clientDialog} onOpenChange={setClientDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Type *</Label>
                <Select value={newClient.type} onValueChange={(v) => setNewClient(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Individual">Individual</SelectItem>
                    <SelectItem value="Corporation">Corporation</SelectItem>
                    <SelectItem value="Others">Others</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <ValidatedInput
                  placeholder={newClient.type === 'Corporation' ? 'Company name' : 'Full name'}
                  value={newClient.name}
                  onChange={(e) => setNewClient(prev => ({ ...prev, name: formatToTitleCase(e.target.value) }))}
                  validationType="name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <ValidatedInput type="email" placeholder="email@example.com" value={newClient.email}
                  onChange={(e) => setNewClient(prev => ({ ...prev, email: formatEmail(e.target.value) }))}
                  validationType="email" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <ValidatedInput placeholder="(647) 212-0990" value={newClient.phone}
                  onChange={(e) => setNewClient(prev => ({ ...prev, phone: formatPhoneNumber(e.target.value) }))}
                  validationType="phone" />
              </div>
            </div>

            {/* Contact Person - for Corporations only */}
            {newClient.type === 'Corporation' && (
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <ValidatedInput
                  placeholder="Primary contact name"
                  value={newClient.contactPerson}
                  onChange={(e) => setNewClient(prev => ({ ...prev, contactPerson: formatToTitleCase(e.target.value) }))}
                  validationType="name"
                  data-testid="contact-person-input"
                />
                <p className="text-xs text-gray-500">The main point of contact for this corporation</p>
              </div>
            )}

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-4">Address</h3>
              <AddressForm
                address={newClient.address}
                onChange={(addr) => setNewClient(prev => ({ ...prev, address: addr }))}
              />
            </div>

            {/* Industry and Referral Source */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-4">Additional Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Industry</Label>
                  <Select value={newClient.industry || 'none'} onValueChange={(v) => setNewClient(prev => ({ ...prev, industry: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {industries.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Referral Source</Label>
                  <Select value={newClient.referralSource || 'none'} onValueChange={(v) => setNewClient(prev => ({ ...prev, referralSource: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select referral source" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {referralSources.map((src) => (
                        <SelectItem key={src} value={src}>{src}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {newClient.type === 'Individual' && (
              <>
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4">Individual Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={newClient.dateOfBirth}
                        max={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setNewClient(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Marital Status</Label>
                      <Select 
                        value={newClient.maritalStatus || 'none'} 
                        onValueChange={(v) => setNewClient(prev => ({ ...prev, maritalStatus: v === 'none' ? '' : v }))}
                      >
                        <SelectTrigger><SelectValue placeholder="Select marital status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not specified</SelectItem>
                          <SelectItem value="Single">Single</SelectItem>
                          <SelectItem value="Married">Married</SelectItem>
                          <SelectItem value="Common Law">Common Law</SelectItem>
                          <SelectItem value="Widowed">Widowed</SelectItem>
                          <SelectItem value="Divorced">Divorced</SelectItem>
                          <SelectItem value="Separated">Separated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>SIN</Label>
                      <ValidatedInput placeholder="123-456-789" value={newClient.sin}
                        onChange={(e) => setNewClient(prev => ({ ...prev, sin: formatSIN(e.target.value) }))}
                        validationType="sin" />
                    </div>
                    <div className="space-y-2">
                      <Label>GST/HST Number</Label>
                      <Input placeholder="123456789RT0001" value={newClient.gstHstNumber}
                        onChange={(e) => setNewClient(prev => ({ ...prev, gstHstNumber: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Payroll Number</Label>
                      <Input placeholder="RP0001" value={newClient.payrollNumber}
                        onChange={(e) => setNewClient(prev => ({ ...prev, payrollNumber: e.target.value }))} />
                    </div>
                  </div>
                </div>

                {/* Spouse Information - Only show if marital status is NOT Single */}
                {newClient.maritalStatus && newClient.maritalStatus !== 'Single' && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-4">Spouse Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Spouse Name</Label>
                        <ValidatedInput placeholder="Spouse full name" value={newClient.spouse.name}
                          onChange={(e) => updateSpouse('name', formatToTitleCase(e.target.value))}
                          validationType="name" />
                      </div>
                      <div className="space-y-2">
                        <Label>Spouse Date of Birth</Label>
                        <Input
                          type="date"
                          value={newClient.spouse.dateOfBirth || ''}
                          max={new Date().toISOString().split('T')[0]}
                          onChange={(e) => updateSpouse('dateOfBirth', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Spouse SIN</Label>
                        <ValidatedInput placeholder="123-456-789" value={newClient.spouse.sin}
                          onChange={(e) => updateSpouse('sin', formatSIN(e.target.value))}
                          validationType="sin" />
                      </div>
                      <div className="space-y-2">
                        <Label>Spouse Email</Label>
                        <ValidatedInput type="email" placeholder="spouse@example.com" value={newClient.spouse.email}
                          onChange={(e) => updateSpouse('email', formatEmail(e.target.value))}
                          validationType="email" />
                      </div>
                      <div className="space-y-2">
                        <Label>Spouse Phone</Label>
                        <ValidatedInput placeholder="(647) 212-0990" value={newClient.spouse.phone}
                          onChange={(e) => updateSpouse('phone', formatPhoneNumber(e.target.value))}
                          validationType="phone" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Children Information - Only show if marital status is NOT Single */}
                {newClient.maritalStatus && newClient.maritalStatus !== 'Single' && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Children Information</h3>
                      <Button type="button" variant="outline" size="sm" onClick={addChild} className="gap-1">
                        <Plus className="h-3 w-3" /> Add Child
                      </Button>
                    </div>
                    {newClient.children.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No children added. Click "Add Child" to add.</p>
                    ) : (
                      <div className="space-y-4">
                        {newClient.children.map((child, index) => (
                          <div key={index} className="p-4 border rounded-lg bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">Child {index + 1}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeChild(index)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Name</Label>
                                <ValidatedInput
                                  placeholder="Child's name"
                                  value={child.name}
                                  onChange={(e) => updateChild(index, 'name', formatToTitleCase(e.target.value))}
                                  validationType="name"
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Date of Birth</Label>
                                <Input
                                  type="date"
                                  value={child.dateOfBirth || ''}
                                  max={new Date().toISOString().split('T')[0]}
                                  onChange={(e) => updateChild(index, 'dateOfBirth', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">SIN</Label>
                                <ValidatedInput
                                  placeholder="123-456-789"
                                  value={child.sin || ''}
                                  onChange={(e) => updateChild(index, 'sin', formatSIN(e.target.value))}
                                  validationType="sin"
                                  className="h-9"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {newClient.type === 'Corporation' && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-4">Corporation Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Business Number (BN)</Label>
                    <Input placeholder="123456789" value={newClient.businessNumber}
                      onChange={(e) => setNewClient(prev => ({ ...prev, businessNumber: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Year End</Label>
                    <Select value={newClient.yearEnd} onValueChange={(v) => setNewClient(prev => ({ ...prev, yearEnd: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select year end" /></SelectTrigger>
                      <SelectContent>
                        {['January 31', 'February 28', 'March 31', 'April 30', 'May 31', 'June 30',
                          'July 31', 'August 31', 'September 30', 'October 31', 'November 30', 'December 31'].map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>GST/HST Number</Label>
                    <Input placeholder="123456789RT0001" value={newClient.gstHstNumber}
                      onChange={(e) => setNewClient(prev => ({ ...prev, gstHstNumber: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Payroll Number</Label>
                    <Input placeholder="RP0001" value={newClient.payrollNumber}
                      onChange={(e) => setNewClient(prev => ({ ...prev, payrollNumber: e.target.value }))} />
                  </div>
                </div>
                
                <div className="mt-4 space-y-2">
                  <Label>Owner (Individual Client)</Label>
                  <ClientSearchInput
                    value={newClient.ownerId}
                    onSelect={(id, name) => setNewClient(prev => ({ ...prev, ownerId: id }))}
                    placeholder="Search individual owner..."
                    typeFilter="Individual"
                    showPhone={true}
                    allowClear={true}
                    data-testid="owner-search-input"
                  />
                  <p className="text-xs text-gray-500">Link this corporation to an existing individual client</p>
                </div>
              </div>
            )}

            {/* Custom Fields Section */}
            {getApplicableCustomFields().length > 0 && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-4">Custom Fields</h3>
                <div className="grid grid-cols-2 gap-4">
                  {getApplicableCustomFields().map((field) => (
                    <div key={field._id} className={`space-y-2 ${(field.type === 'conditional_number' || field.type === 'conditional_dropdown') ? 'col-span-2' : ''}`}>
                      <Label>
                        {field.name}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      {(field.type === 'conditional_number' || field.type === 'conditional_dropdown') ? (
                        <ConditionalField
                          field={field}
                          value={newClient.customFields[field.fieldId]}
                          onChange={(v) => updateCustomField(field.fieldId, v)}
                        />
                      ) : (field.type === 'select' || field.type === 'dropdown') ? (
                        <Select 
                          value={newClient.customFields[field.fieldId] || ''} 
                          onValueChange={(v) => updateCustomField(field.fieldId, v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Select ${field.name}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {(field.options || []).map((option) => {
                              // Handle both string options and object options with label
                              const optionValue = typeof option === 'string' ? option : option.label;
                              const optionKey = typeof option === 'string' ? option : option.id;
                              return (
                                <SelectItem key={optionKey} value={optionValue}>{optionValue}</SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      ) : field.type === 'date' ? (
                        <Input 
                          type="date"
                          value={newClient.customFields[field.fieldId] || ''}
                          onChange={(e) => updateCustomField(field.fieldId, e.target.value)}
                        />
                      ) : field.type === 'number' ? (
                        <Input 
                          type="number"
                          placeholder={`Enter ${field.name}`}
                          value={newClient.customFields[field.fieldId] || ''}
                          onChange={(e) => updateCustomField(field.fieldId, e.target.value)}
                        />
                      ) : field.type === 'checkbox' ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={newClient.customFields[field.fieldId] === 'true' || newClient.customFields[field.fieldId] === true}
                            onChange={(e) => updateCustomField(field.fieldId, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-600">Yes</span>
                        </div>
                      ) : (
                        <Input 
                          placeholder={`Enter ${field.name}`}
                          value={newClient.customFields[field.fieldId] || ''}
                          onChange={(e) => updateCustomField(field.fieldId, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button onClick={handleCreateClient} className="flex-1">Create Client</Button>
              <Button variant="outline" onClick={() => setClientDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{clientToDelete?.name}</strong>? 
              This action cannot be undone and will remove all associated data including invoices, tasks, and documents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clients;
